//src/pages/inicio.js

import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import MetasWidget from '../components/widgets/MetasWidget';
import EstadisticasPedidosWidget from '../components/widgets/EstadisticasPedidosWidget';
import EstadisticasDiaWidget from '../components/widgets/EstadisticasDiaWidget';
import RecordPedidosWidget from '../components/widgets/RecordPedidosWidget';
import PulsoPublicidadWidget from '../components/widgets/PulsoPublicidadWidget';
import PulsoDevolucionesWidget from '../components/widgets/PulsoDevolucionesWidget';
import AsistenciaHoyWidget from '../components/widgets/AsistenciaHoyWidget';
import ReposicionWidget from '../components/widgets/ReposicionWidget';
import CumpleanosWidget from '../components/widgets/CumpleanosWidget';
import MarcarAsistenciaMovil from '../components/asistencia/MarcarAsistenciaMovil';
import { MapPinIcon } from '../components/ui/Icons';

// Config de la marca móvil, cacheada en localStorage 10 min para no leerla en cada visita.
const CFG_KEY = 'asistencia_marcamovil_cfg';
const CFG_TTL = 10 * 60 * 1000;
async function cargarConfigMovil() {
  try {
    const raw = localStorage.getItem(CFG_KEY);
    if (raw) { const o = JSON.parse(raw); if (o && (Date.now() - o._ts) < CFG_TTL) return o.cfg; }
  } catch (e) { /* noop */ }
  try {
    const s = await getDoc(doc(db, 'asistencia_config', 'marcaMovil'));
    const cfg = s.exists() ? s.data() : null;
    try { localStorage.setItem(CFG_KEY, JSON.stringify({ _ts: Date.now(), cfg })); } catch (e) { /* noop */ }
    return cfg;
  } catch (e) { return null; }
}

// Exclusiones del control de asistencia (deptos/colaboradores), mismo esquema de caché:
// un colaborador excluido no debe ver el botón de marcar.
const EX_KEY = 'asistencia_exclusiones_cfg';
async function cargarExclusiones() {
  try {
    const raw = localStorage.getItem(EX_KEY);
    if (raw) { const o = JSON.parse(raw); if (o && (Date.now() - o._ts) < CFG_TTL) return o.ex; }
  } catch (e) { /* noop */ }
  try {
    const s = await getDoc(doc(db, 'asistencia_config', 'exclusiones'));
    const ex = s.exists() ? s.data() : null;
    try { localStorage.setItem(EX_KEY, JSON.stringify({ _ts: Date.now(), ex })); } catch (e) { /* noop */ }
    return ex;
  } catch (e) { return null; }
}

function Inicio() {
  const { usuarioActual, permisosUsuario, puedeAbrirModulo } = useOutletContext();

  // Widgets por PERMISO (sección WIDGETS de Gestión de Permisos). Los admins
  // los ven todos (permisosUsuario === 'todos'); el resto según lo asignado.
  const tieneWidget = (id) =>
    permisosUsuario === 'todos' || (Array.isArray(permisosUsuario) && permisosUsuario.includes(id));
  // El permiso del widget no da el módulo: su botón "Abrir …" sale solo si el candado de ruta (Layout.js) deja abrir
  // ese módulo. Si no, el widget se ve igual, sin el botón.
  const puedeAbrir = (moduloId) => typeof puedeAbrirModulo === 'function' && puedeAbrirModulo(moduloId);
  const [cfgMovil, setCfgMovil] = useState(null);
  const [exclusiones, setExclusiones] = useState(null);
  const [configCargada, setConfigCargada] = useState(false);
  const [marcando, setMarcando] = useState(false);

  useEffect(() => {
    let vivo = true;
    // Cargar ambas configs juntas: el botón no se pinta hasta saber config + exclusiones,
    // así no aparece y desaparece (un excluido nunca lo ve parpadear).
    Promise.all([cargarConfigMovil(), cargarExclusiones()]).then(([cfg, ex]) => {
      if (!vivo) return;
      setCfgMovil(cfg);
      setExclusiones(ex);
      setConfigCargada(true);
    });
    return () => { vivo = false; };
  }, []);

  // Excluido del control de asistencia (él o su departamento) → no marca, sin botón.
  const estaExcluido = !!(exclusiones && usuarioActual && (
    (exclusiones.usuarios || []).some((e) => (e || '').toLowerCase() === (usuarioActual.email || '').toLowerCase()) ||
    (!!usuarioActual.departamentoEmpresaId && (exclusiones.departamentos || []).includes(usuarioActual.departamentoEmpresaId))
  ));
  // Anti-parpadeo: usuarioActual llega async desde Layout (query a Firestore). Sin él NO se
  // puede evaluar la exclusión (daría false) ni la preferencia de escritorio, así que el botón
  // no se pinta hasta tenerlo — un excluido nunca lo ve aparecer y desaparecer.
  const mostrarMarca = configCargada && !!usuarioActual && !!(cfgMovil && cfgMovil.habilitado && cfgMovil.oficinaLat && cfgMovil.oficinaLng) && !estaExcluido;
  // Preferencia personal (Mi Perfil): mostrar el botón también en la computadora.
  const marcaEnEscritorio = usuarioActual?.marcaAsistenciaEscritorio === true;

  return (
    <div className="welcome-screen">
      {mostrarMarca && (
        <>
          {/* Por defecto solo móvil (la marca usa GPS y cámara del teléfono). Si el colaborador
              activó "Marcar asistencia en la computadora" en Mi Perfil, se omite este CSS y el
              display:flex inline del botón lo muestra también en escritorio. */}
          {!marcaEnEscritorio && (
            <style>{`
              .btn-marca-movil-inicio { display: none !important; }
              @media (max-width: 768px) {
                .btn-marca-movil-inicio { display: flex !important; }
              }
            `}</style>
          )}
          <button
            onClick={() => setMarcando(true)}
            className="btn-marca-movil-inicio btn-press hover-bright"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              margin: '0 auto 26px auto', padding: '16px 32px', maxWidth: '360px', width: '100%',
              background: 'var(--color-primario)', color: 'white', border: 'none', borderRadius: '12px',
              fontSize: '18px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(102,126,234,0.35)'
            }}
          >
            <MapPinIcon className="w-6 h-6" /> Marcar asistencia
          </button>
        </>
      )}

      {/* Cumpleaños del equipo: visible para TODOS (sin permiso, como el Manual).
          Si no hay cumpleaños hoy ni en 7 días, no ocupa espacio. */}
      <CumpleanosWidget />

      {usuarioActual?.rol === 'financiero' || usuarioActual?.rol === 'supervisor' || usuarioActual?.rol === 'admin' ? (
        <EstadisticasDiaWidget />
      ) : null}
      {usuarioActual?.rol === 'servicio_cliente' && (
        <>
          <MetasWidget />
          <EstadisticasPedidosWidget />
        </>
      )}

      {/* Widgets por permiso (sección WIDGETS en Gestión de Permisos) */}
      {tieneWidget('widget-record-pedidos') && <RecordPedidosWidget />}
      {tieneWidget('widget-ads-insights') && <PulsoPublicidadWidget conEnlace={puedeAbrir('ads-dashboard')} />}
      {tieneWidget('widget-devoluciones') && <PulsoDevolucionesWidget conEnlace={puedeAbrir('analisis-devoluciones')} />}
      {tieneWidget('widget-asistencia-hoy') && <AsistenciaHoyWidget conEnlace={puedeAbrir('asistencia')} />}
      {tieneWidget('widget-reposicion') && <ReposicionWidget conEnlace={puedeAbrir('alertas-reposicion')} />}

      {marcando && <MarcarAsistenciaMovil config={cfgMovil} onClose={() => setMarcando(false)} />}
    </div>
  );
}

export default Inicio;
